import React, { useState, useEffect } from 'react';
import {
  getExtraCurricular,
  addExtraCurricular,
  updateExtraCurricular,
  deleteExtraCurricular
} from '../../services/profile';
import { FaEdit, FaTrashAlt, FaPlus, FaTimes, FaSave, FaUsers, FaTrophy } from 'react-icons/fa';
import './ExtraCurricular.css';

const ExtraCurricular = ({ profileSection, profileData, setCompletion }) => {
  const [extraCurricularList, setExtraCurricularList] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // State for new/editing extra curricular activity
  const [activityForm, setActivityForm] = useState({
    activity_name: '',
    organization: '',
    role: '',
    start_date: '',
    end_date: '',
    is_current: false,
    description: '',
    achievements: ''
  });

  // Fetch extracurricular activities on component mount
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setIsLoading(true);
        const response = await getExtraCurricular( profileData?.profile_id);
        setExtraCurricularList(response);
      } catch (error) {
        console.error('Error fetching extracurricular activities:', error);
        setFormError('Failed to load activities. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivities();
  }, []);

  // Clear form data
  const clearForm = () => {
    setActivityForm({
      activity_name: '',
      organization: '',
      role: '',
      start_date: '',
      end_date: '',
      is_current: false,
      description: '',
      achievements: ''
    });
    setFormError('');
  };

  useEffect(() => {
    setCompletion("extra", extraCurricularList);
  }, [extraCurricularList])

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setActivityForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // If setting current activity to true, clear end date
    if (name === 'is_current' && checked) {
      setActivityForm(prev => ({
        ...prev,
        end_date: ''
      }));
    }
  };

  // Add new extra curricular activity
  const handleAddActivity = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!activityForm.activity_name || !activityForm.organization || !activityForm.start_date ||
      (!activityForm.end_date && !activityForm.is_current)) {
      setFormError('Please fill out all required fields');
      return;
    }

    try {
      // Format data for API
      const activityData = {
        activity_name: activityForm.activity_name,
        organization: activityForm.organization,
        role: activityForm.role || null,
        start_date: activityForm.start_date,
        end_date: activityForm.is_current ? null : activityForm.end_date,
        description: activityForm.description || null
      };

      // Send to API
      const response = await addExtraCurricular(activityData, profileData?.profile_id);

      // Update local state with response
      setExtraCurricularList(prev => [...prev, response]);

      // Reset form and state
      clearForm();
      setIsAdding(false);
    } catch (error) {
      console.error('Error adding extracurricular activity:', error);
      setFormError('Failed to add activity. Please try again.');
    }
  };

  // Start editing an activity
  const handleEditStart = (activity) => {
    setIsEditing(activity.id);

    setActivityForm({
      activity_name: activity.activity_name,
      organization: activity.organization || '',
      role: activity.role || '',
      start_date: activity.start_date || '',
      end_date: activity.end_date || '',
      is_current: !activity.end_date,
      description: activity.description || '',
      achievements: activity.achievements || ''
    });
  };

  // Save edited activity
  const handleEditSave = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!activityForm.activity_name || !activityForm.organization || !activityForm.start_date ||
      (!activityForm.end_date && !activityForm.is_current)) {
      setFormError('Please fill out all required fields');
      return;
    }

    try {
      // Format data for API
      const activityData = {
        activity_name: activityForm.activity_name,
        organization: activityForm.organization,
        role: activityForm.role || null,
        start_date: activityForm.start_date,
        end_date: activityForm.is_current ? null : activityForm.end_date,
        description: activityForm.description || null
      };

      // Update activity
      const response = await updateExtraCurricular(isEditing, activityData, profileData?.profile_id);

      // Update the activity in the local state
      setExtraCurricularList(prev =>
        prev.map(act => act.id === isEditing ? response : act)
      );

      // Reset form and state
      clearForm();
      setIsEditing(null);
    } catch (error) {
      console.error('Error updating extracurricular activity:', error);
      setFormError('Failed to update activity. Please try again.');
    }
  };

  // Cancel adding/editing
  const handleCancel = () => {
    clearForm();
    setIsAdding(false);
    setIsEditing(null);
  };

  // Delete activity
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this activity?')) {
      try {
        await deleteExtraCurricular(id, profileData?.profile_id);
        setExtraCurricularList(prev => prev.filter(activity => activity.id !== id));
      } catch (error) {
        console.error('Error deleting extracurricular activity:', error);
        setFormError('Failed to delete activity. Please try again.');
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="extracurricular-loading">
        <div className="loading-spinner"></div>
        <p>Loading extracurricular activities...</p>
      </div>
    );
  }

  return (
    <div className="extracurricular-section">
      <div className="section-header">
        <h2><div className="section-icon">
          {profileSection.icon}
        </div> Extracurricular Activities</h2>
        {!isAdding && !isEditing && (
          <button
            onClick={() => setIsAdding(true)}
            className="btn-add"
          >
            <FaPlus /> Add Activity
          </button>
        )}
      </div>

      {/* Extra Curricular List */}
      {extraCurricularList.length > 0 ? (
        <div className="extracurricular-list">
          {extraCurricularList.map((activity) => (
            <div key={activity.id} className="extracurricular-item">
              {isEditing === activity.id ? (
                <form onSubmit={handleEditSave} className="extracurricular-form">
                  <h3>Edit Activity</h3>
                  {formError && <div className="form-error">{formError}</div>}

                  <div className="form-group">
                    <label htmlFor="activity_name">Activity Name *</label>
                    <input
                      type="text"
                      id="activity_name"
                      name="activity_name"
                      value={activityForm.activity_name}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="organization">Organization *</label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={activityForm.organization}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="role">Role/Position</label>
                    <input
                      type="text"
                      id="role"
                      name="role"
                      value={activityForm.role}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="start_date">Start Date *</label>
                      <input
                        type="date"
                        id="start_date"
                        name="start_date"
                        value={activityForm.start_date}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="end_date">End Date {!activityForm.is_current && '*'}</label>
                      <input
                        type="date"
                        id="end_date"
                        name="end_date"
                        value={activityForm.end_date}
                        onChange={handleInputChange}
                        disabled={activityForm.is_current}
                        required={!activityForm.is_current}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group checkbox">
                    <div className="current-checkbox">
                        <input
                      type="checkbox"
                      id="is_current"
                      name="is_current"
                      checked={activityForm.is_current}
                      onChange={handleInputChange}
                      className="checkbox-input"
                    />
                    <label htmlFor="is_current" className="checkbox-label">I'm currently involved in this activity</label>
                  </div>
                  </div>
                  </div>

                  

                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={activityForm.description}
                      onChange={handleInputChange}
                      rows="3"
                      className="form-control"
                    />
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="btn-secondary">
                      <FaTimes /> Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      <FaSave /> Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="extracurricular-display">
                  <div className="extracurricular-header">
                    <div className="extracurricular-title">
                      <h4>{activity.activity_name}</h4>
                      <p className="extracurricular-organization">
                        {activity.organization}
                        {activity.role && ` - ${activity.role}`}
                      </p>
                      <p className="extracurricular-dates">
                        {formatDate(activity.start_date)} - {!activity.end_date ? 'Present' : formatDate(activity.end_date)}
                      </p>
                    </div>
                    <div className="extracurricular-actions">
                      <button
                        onClick={() => handleEditStart(activity)}
                        className="btn-icon edit"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(activity.id)}
                        className="btn-icon delete"
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>

                  {activity.description && (
                    <div className="extracurricular-details">
                      <div className="extracurricular-description">
                        <p>{activity.description}</p>
                      </div>

                      {activity.achievements && (
                        <div className="extracurricular-achievements">
                          <p><FaTrophy className="achievement-icon" /> <strong>Achievements:</strong> {activity.achievements}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-list">
          <p>No extracurricular activities added yet.</p>
          <p>Click "Add Activity" to get started.</p>
        </div>
      )}

      {/* Add Activity Form */}
      {isAdding && (
        <div className="add-extracurricular">
          <form onSubmit={handleAddActivity} className="extracurricular-form">
            <h3>Add New Activity</h3>
            {formError && <div className="form-error">{formError}</div>}

            <div className="form-group">
              <label htmlFor="activity_name">Activity Name *</label>
              <input
                type="text"
                id="activity_name"
                name="activity_name"
                value={activityForm.activity_name}
                onChange={handleInputChange}
                required
                className="form-control"
                placeholder="e.g., Volunteer Work, Club Membership, Sports Team"
              />
            </div>

            <div className="form-group">
              <label htmlFor="organization">Organization *</label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={activityForm.organization}
                onChange={handleInputChange}
                required
                className="form-control"
                placeholder="Organization or group name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="role">Role/Position</label>
              <input
                type="text"
                id="role"
                name="role"
                value={activityForm.role}
                onChange={handleInputChange}
                className="form-control"
                placeholder="e.g., Team Captain, Treasurer, Member"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">Start Date *</label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={activityForm.start_date}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="end_date">End Date {!activityForm.is_current && '*'}</label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={activityForm.end_date}
                  onChange={handleInputChange}
                  disabled={activityForm.is_current}
                  required={!activityForm.is_current}
                  className="form-control"
                />
              </div>
              <div className="form-group checkbox">
              <div className="current-checkbox">
                        <input
                type="checkbox"
                id="is_current"
                name="is_current"
                checked={activityForm.is_current}
                onChange={handleInputChange}
                className="checkbox-input"
              />
              <label htmlFor="is_current" className="checkbox-label">I'm currently involved in this activity</label>
            </div>
            </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={activityForm.description}
                onChange={handleInputChange}
                rows="3"
                className="form-control"
                placeholder="Describe the activity, your involvement, and key responsibilities"
              />
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-secondary">
                <FaTimes /> Cancel
              </button>
              <button type="submit" className="btn-primary">
                <FaPlus /> Add Activity
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ExtraCurricular;